import React, {
  useState,
  useEffect,
  useMemo,
  Fragment,
  useCallback,
} from "react";
import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  LinearProgress,
  Button,
  Collapse,
} from "@mui/material";
import ReactPaginate from "react-paginate";
import Table from "../../Components/Common/Table/Table";
import {
  getInfoMessageNoResultsSearch,
  CommonSubHeader,
} from "../../Components/helpers/Helpers";
import Swal from "sweetalert2";
import TableActions from "../../Components/Common/Table/TableActions";
import api from "../../api/axios";
import {
  ChevronLeft,
  ChevronRight,
  TextIcon,
  Filter,
  RotateCcw,
  Search,
  Delete,
} from "lucide-react";
import { Box } from "@mui/system";
import {
  CleaningServices,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";

function HojasDeEncargo({ flagToShow = true }) {
  const [contracts, setContracts] = useState([]);
  const [objPerPage, setObjPerPage] = useState(10);
  const [pagePressed, setPagePressed] = useState(1);
  const [totalPagesNumber, setTotalPagesNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [flag, setFlag] = useState(false);
  const [changeDone, setChangeDone] = useState(false);

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const handleSort = (column, direction) => {
    console.log(column, direction);
    setSortColumn(column);
    setSortDirection(direction);
    setPagePressed(1); // reinicia paginación
  };

  // Filtros aplicados (los reales)
  const [filters, setFilters] = useState({
    company: 0,
    contract_title: "",
  });

  // Filtros temporales (los del input)
  const [draftFilters, setDraftFilters] = useState({
    company: 0,
    contract_title: "",
  });

  const [options, setOptions] = useState({
    companies: [],
  });

  const [loadingOptions, setLoadingOptions] = useState({
    companies: false,
  });

  const contractsData = useCallback(async () => {
    setFlag(true);
    try {
      const response = await api.post(
        process.env.REACT_APP_API_URL + "/contracts",
        {
          page: pagePressed,
          objPerPage,
          sortColumn,
          sortDirection,
          ...filters,
        },
      );

      const { contracts, companies } = response.data;

      companies.unshift({ company_name: "Seleccionar", id: 0 });

      setContracts(contracts.data);
      setPageCount(contracts.last_page);
      setTotalPagesNumber(contracts.total);
      setOptions((prev) => ({ ...prev, companies }));
    } catch (error) {
      console.error("ERROR AXIOS:", error);

      if (error.response) {
        console.error("STATUS:", error.response.status);
        console.error("DATA:", error.response.data);
        console.error("HEADERS:", error.response.headers);
      } else if (error.request) {
        console.error("NO RESPONSE:", error.request);
      } else {
        console.error("REQUEST ERROR:", error.message);
      }
      Swal.fire({
        icon: "error",
        title: "Error en el servidor",
        text: "Hubo un problema al cargar los datos.",
      });
    } finally {
      setFlag(false);
    }
  }, [pagePressed, objPerPage, filters, changeDone, sortColumn, sortDirection]);

  useEffect(() => {
    contractsData();
  }, [contractsData]);

  const handlePageClick = (event) => setPagePressed(event.selected + 1);

  // Solo modifica el draft
  const handleDraftChange = (name, value) => {
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Aplicar filtros
  const applyFilters = () => {
    setPagePressed(1);
    setFilters(draftFilters);
  };

  // Limpiar filtros
  const clearFilters = () => {
    const reset = {
      company: 0,
      contract_title: "",
    };
    setDraftFilters(reset);
    setFilters(reset);
    setPagePressed(1);
  };

  const renderCellWithDefault = (value, defaultValue = "--") => (
    <div className="spanClassForTables" style={{ paddingLeft: "20px" }}>
      {value || defaultValue}
    </div>
  );

  const columns = useMemo(
    () => [
      {
        Header: "LISTA DE HOJAS DE ENCARGO",
        columns: [
          {
            Header: "Empresa",
            accessor: "company_relation",
            sortField: "company_name",
            width: 200,
            Cell: ({ value }) => (
              <div className="spanClassForTables">
                <b>{value?.company_name}</b>
              </div>
            ),
          },
          {
            Header: "Hoja de Encargo/Contrato",
            sortField: "contract_title",
            accessor: "contract_title",
            width: 500,
            Cell: ({ value }) => renderCellWithDefault(value),
          },
          {
            Header: "Acciones",
            accessor: "id",
            Cell: ({ value }) => (
              <TableActions
                rute={"contratos"}
                value={value}
                showView
                showEdit
                showDelete
                module={"contracts"}
                apiRute={"contracts"}
                setChangeDone={setChangeDone}
                changeDone={changeDone}
              />
            ),
          },
        ],
      },
    ],
    [],
  );

  const content = (
    <div className="fatherContainer">
      <div className="mainContainer">
        <div className="subMainContainer">
          {(contracts === "" || flag) && <LinearProgress color="success" />}

          <Box
            sx={{
              position: "relative",
              background: "white",
              marginBottom: "33px",
              mx: "10px",
              borderRadius: "10px",
            }}>
            <Collapse in={showFilters} timeout={400}>
              <Grid
                container
                className="filtersContainer"
                sx={{
                  boxShadow: `0 1px 2px rgba(0,0,0,0.05), 0 -8px 38px rgba(0,0,0,0.08)`,
                  justifyContent: "center",
                  padding: "10px",
                  gap: 2,
                }}>
                {/* Empresa */}
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    size="small"
                    options={options.companies || []}
                    value={
                      options.companies.find(
                        (c) => c.id === draftFilters.company,
                      ) || null
                    }
                    onChange={(_, value) =>
                      handleDraftChange("company", value ? value.id : 0)
                    }
                    getOptionLabel={(option) => option.company_name || ""}
                    renderInput={(params) => (
                      <TextField {...params} label="Empresas" />
                    )}
                  />
                </Grid>

                {/* Texto Hoja de Encargo */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hoja de Encargo"
                    value={draftFilters.contract_title}
                    onChange={(e) =>
                      handleDraftChange("contract_title", e.target.value)
                    }
                  />
                </Grid>

                {/* Nº registros */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Nº Registros</InputLabel>
                    <Select
                      value={objPerPage}
                      label="Nº Registros"
                      onChange={(e) => {
                        setObjPerPage(Number(e.target.value));
                        setPagePressed(1);
                      }}>
                      {[10, 20, 30, 100, 200, 500, 1000, 2000].map((num) => (
                        <MenuItem key={num} value={num}>
                          {num}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Botones */}
                <Grid
                  item
                  xs={12}
                  textAlign="center"
                  sx={{
                    display: "inline-flex",
                    gap: "10px",
                    justifyContent: "center",
                    marginTop: 1,
                  }}>
                  <CustomButtonPlusIcon
                    icon={Search}
                    text="BUSCAR"
                    handleFunction={applyFilters}
                    position="start"
                    variant="outlined"
                    color="info"
                  />
                  <CustomButtonPlusIcon
                    icon={CleaningServices}
                    text="LIMPIAR FILTROS"
                    handleFunction={clearFilters}
                    position="start"
                    variant="outlined"
                    color="error"
                  />
                </Grid>
              </Grid>
            </Collapse>

            {/* Botón flotante */}
            <Box
              sx={{
                position: "absolute",
                bottom: showFilters ? "-26px" : "-16px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
              }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                endIcon={
                  showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />
                }
                sx={{
                  minWidth: "220px",
                  height: "25px",
                  borderRadius: "0px 0px 10px 10px",
                  boxShadow: 3,
                  textTransform: "none",
                  fontWeight: 500,
                }}>
                {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
              </Button>
            </Box>
          </Box>

          {contracts && !flag && (
            <>
              <Table
                columns={columns}
                data={contracts}
                view={"contrato"}
                viewTitle={"contratos"}
                totalPagesNumber={totalPagesNumber}
                icon={TextIcon}
                apiRoute={"contracts"}
                viewPermission={"contracts"}
                // pagination
                onPageChange={handlePageClick}
                pageCount={pageCount}
                forcePage={pagePressed - 1} // Esto sincroniza el botón activo
                // SORT
                onSort={handleSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                objPerPage={objPerPage}
                tableWidth="unset"
                minTableWidth="80%"
                setChangeDone={setChangeDone}
                changeDone={changeDone}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );

  return flagToShow ? (
    <div>
      <CommonSubHeader
        title="Configuración"
        subTitle="Hojas de Encargo - Contratos"
      />
      {content}
    </div>
  ) : (
    content
  );
}

export default HojasDeEncargo;
